import { apiClient } from '@/lib/api';
import React, { useEffect, useRef, useState } from 'react';

const SelfieCapture: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [mode, setMode] = useState<'start' | 'live' | 'preview' | 'uploading'>('start');
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Parse token from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');

    if (!urlToken) {
      setError('Invalid capture link - no token provided');
      return;
    }

    // Validate the token decodes before accepting it
    try {
      JSON.parse(atob(urlToken.split('.')[1]));
      setToken(urlToken);
    } catch (e) {
      setError('Invalid capture token');
    }
  }, []);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const startCamera = async () => {
    try {
      setError(null);

      // Ensure video is mounted
      setMode('live');
      // Allow React to render the video before we touch ref
      await new Promise(requestAnimationFrame);

      // Stop any old tracks
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      // Request camera (front-facing for selfie)
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'user' } },
          audio: false,
        });
      } catch (facingModeErr) {
        // Fallback for devices that don't support facingMode
        console.warn('FacingMode failed, trying plain video:', facingModeErr);
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }

      streamRef.current = stream;

      const v = videoRef.current;
      if (!v) throw new Error('Video element not mounted');
      v.srcObject = stream;
      v.setAttribute('playsinline', 'true');
      v.muted = true;
      v.autoplay = true;

      // Wait for metadata → play (iOS/Safari needs this in user gesture)
      await new Promise<void>((resolve) => {
        const onMeta = () => {
          v.removeEventListener('loadedmetadata', onMeta);
          console.log('📹 Video metadata loaded:', v.videoWidth, 'x', v.videoHeight);
          resolve();
        };
        v.addEventListener('loadedmetadata', onMeta);
      });

      await v.play().catch(() => {});

      // Debug video state
      console.log('📹 Video ready state:', v.readyState, 'Size:', v.videoWidth, 'x', v.videoHeight);
    } catch (err) {
      console.error('Camera access failed:', err);
      setMode('start'); // Go back to start on error

      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access and try again.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found. Please ensure your device has a camera.');
        } else {
          setError(`Camera access failed: ${err.message}. Please check your camera and try again.`);
        }
      } else {
        setError('Camera access failed. Please try again.');
      }
    }
  };

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const onCapture = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      setError('Canvas not supported');
      return;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0);

    // Convert to blob and create preview URL
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Failed to capture image');
          return;
        }

        setCapturedBlob(blob);
        const previewURL = URL.createObjectURL(blob);
        setPreviewUrl(previewURL);
        stopTracks();
        setMode('preview');
      },
      'image/jpeg',
      0.92
    );
  };

  const onRetake = async () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setCapturedBlob(null);
    await startCamera();
  };

  const onConfirm = async () => {
    if (!capturedBlob || !token) return;

    try {
      setMode('uploading');

      const formData = new FormData();
      formData.append('file', capturedBlob);
      formData.append('token', token);

      const result = (await apiClient.post(`/agreements/selfie-upload`, formData)) as any;

      console.log('✅ Upload successful:', result);

      // Notify parent window and close
      const messageData = {
        type: 'besty:selfieUploaded',
        url: result.publicUrl,
      };

      if (window.opener) {
        window.opener.postMessage(messageData, window.location.origin);
      } else if (window.parent && window.parent !== window) {
        window.parent.postMessage(messageData, window.location.origin);
      }

      stopTracks();
      window.close();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
      setMode('preview'); // Go back to preview to allow retry
    }
  };

  if (!token) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading...</h2>
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '1rem',
        maxWidth: '640px',
        margin: '0 auto',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}> Take a selfie so we can verify your identity</h2>

      {/* Always render video/canvas, toggle visibility with CSS */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <video
          ref={videoRef}
          style={{
            width: '100%',
            maxWidth: '480px',
            height: '360px',
            display: mode === 'live' ? 'block' : 'none',
            background: '#000',
            borderRadius: '12px',
            margin: '0 auto',
            objectFit: 'cover',
          }}
          playsInline
          muted
          autoPlay
        />
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            maxWidth: '480px',
            height: '360px',
            display: 'none',
            borderRadius: '12px',
            margin: '0 auto',
          }}
        />
        {mode === 'preview' && previewUrl && (
          <img
            src={previewUrl}
            style={{
              width: '100%',
              maxWidth: '480px',
              height: '360px',
              display: 'block',
              borderRadius: '12px',
              margin: '0 auto',
              objectFit: 'cover',
            }}
            alt="Captured selfie preview"
          />
        )}
      </div>

      {mode === 'start' && (
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
            Please take a clear photo of your face, centered on the frame.
          </p>

          <button
            type="button"
            onClick={startCamera}
            style={{
              background: 'var(--besty-blue)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '1rem 2rem',
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            Start Camera
          </button>

          {error && (
            <div
              style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
              }}
            >
              <p style={{ color: '#ef4444', margin: 0 }}>{error}</p>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  startCamera();
                }}
                style={{
                  background: 'var(--besty-blue)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  marginTop: '0.5rem',
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {mode === 'live' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={onCapture}
              style={{
                background: 'var(--besty-blue)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '1rem 2rem',
                fontSize: '1rem',
                cursor: 'pointer',
              }}
            >
              Capture
            </button>
          </div>
        </div>
      )}

      {mode === 'preview' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={onRetake}
              style={{
                background: '#ffffff',
                color: '#111827',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                padding: '1rem 2rem',
                fontSize: '1rem',
                cursor: 'pointer',
              }}
            >
              Retake
            </button>

            <button
              type="button"
              onClick={onConfirm}
              style={{
                background: 'var(--besty-blue)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '1rem 2rem',
                fontSize: '1rem',
                cursor: 'pointer',
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {mode === 'uploading' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p>Uploading selfie...</p>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Please wait...</p>
        </div>
      )}

      {error && mode !== 'live' && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#ef4444', margin: '0 0 0.5rem 0' }}>{error}</p>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (mode === 'preview') {
                // Stay in preview mode, just clear error
              } else {
                startCamera();
              }
            }}
            style={{
              background: 'var(--besty-blue)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default SelfieCapture;
